import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import EmptyState from './EmptyState'
import Skeleton from './Skeleton'
import styles from './DataTable.module.scss'

/**
 * Универсальная таблица с сортировкой, фильтрацией и пагинацией.
 *
 * Использование:
 *   <DataTable
 *     columns={[
 *       { key: 'name', label: 'ФИО', sortable: true },
 *       { key: 'group', label: 'Группа', sortable: true, filter: true },
 *       { key: 'gpa', label: 'GPA', sortable: true, align: 'right',
 *         render: (v) => v.toFixed(2) },
 *       { key: 'actions', label: '', render: (_, row) => <button>Open</button> },
 *     ]}
 *     data={students}
 *     loading={loading}
 *     pageSize={20}
 *     onRowClick={(row) => navigate(`/student/${row.id}`)}
 *     emptyText="Студенты не найдены"
 *   />
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  pageSize = 20,
  onRowClick,
  emptyText,
  emptyDescription = '',
  searchable = true,
  searchPlaceholder,
  className = '',
  rowKey = 'id',
  variant = 'default',
}) => {
  const { t } = useTranslation('common')
  const resolvedEmptyText = emptyText ?? t('auto.unvui_datatable.empty', 'Нет данных')
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('auto.unvui_datatable.search', 'Поиск...')
  const [sort, setSort] = useState({ key: null, dir: 'asc' })
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [columnFilters, setColumnFilters] = useState({})

  const sortedFiltered = useMemo(() => {
    let rows = Array.isArray(data) ? [...data] : []

    // Search across all string-renderable fields
    if (search) {
      const q = search.toLowerCase()
      rows = rows.filter((r) =>
        columns.some((c) => {
          const v = r[c.key]
          if (v === null || v === undefined) return false
          return String(v).toLowerCase().includes(q)
        }),
      )
    }

    // Per-column filters
    for (const [key, val] of Object.entries(columnFilters)) {
      if (!val) continue
      const v = String(val).toLowerCase()
      rows = rows.filter((r) => String(r[key] ?? '').toLowerCase().includes(v))
    }

    // Sort
    if (sort.key) {
      rows.sort((a, b) => {
        const av = a[sort.key]
        const bv = b[sort.key]
        if (av === null || av === undefined) return 1
        if (bv === null || bv === undefined) return -1
        if (typeof av === 'number' && typeof bv === 'number') {
          return sort.dir === 'asc' ? av - bv : bv - av
        }
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true })
        return sort.dir === 'asc' ? cmp : -cmp
      })
    }
    return rows
  }, [data, columns, search, columnFilters, sort])

  const total = sortedFiltered.length
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = sortedFiltered.slice(safePage * pageSize, safePage * pageSize + pageSize)

  const handleSort = (key) => {
    setSort((s) => {
      if (s.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      return { key, dir: 'asc' }
    })
  }

  if (loading) {
    return (
      <div className={`${styles.wrap} ${className}`}>
        <Skeleton variant="table" rows={6} cols={columns.length || 4} />
      </div>
    )
  }

  return (
    <div className={`${styles.wrap} ${styles[variant] || ''} ${className}`}>
      {searchable && (
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Icon name="search" size={16} color="var(--color-text-muted)" />
            <input
              type="text"
              placeholder={resolvedSearchPlaceholder}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0) }}
              className={styles.searchInput}
              aria-label={resolvedSearchPlaceholder}
            />
            {search && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => setSearch('')}
                aria-label={t('auto.unvui_datatable.clear_search', 'Очистить поиск')}
              >
                <Icon name="x" size={14} />
              </button>
            )}
          </div>
          <div className={styles.totalCount}>
            {total > 0 ? `${total}` : ''}
          </div>
        </div>
      )}

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{ textAlign: c.align || 'left', width: c.width }}
                  className={c.sortable ? styles.sortable : ''}
                  onClick={() => c.sortable && handleSort(c.key)}
                  scope="col"
                >
                  <span className={styles.thInner}>
                    <span>{c.label}</span>
                    {c.sortable && (
                      <span className={styles.sortIcon}>
                        {sort.key === c.key
                          ? <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} size={14} />
                          : <Icon name="chevron-down" size={14} color="var(--color-text-muted)" />}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
            {columns.some((c) => c.filter) && (
              <tr className={styles.filterRow}>
                {columns.map((c) => (
                  <th key={c.key + '_filter'}>
                    {c.filter ? (
                      <input
                        type="text"
                        className={styles.filterInput}
                        placeholder={t('auto.unvui_datatable.filter', 'Фильтр')}
                        value={columnFilters[c.key] || ''}
                        onChange={(e) => {
                          setColumnFilters((f) => ({ ...f, [c.key]: e.target.value }))
                          setPage(0)
                        }}
                        aria-label={t('auto.unvui_datatable.filter_by_field', 'Фильтр по полю {{label}}', { label: c.label })}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.emptyCell}>
                  <EmptyState
                    iconName="folder"
                    title={resolvedEmptyText}
                    description={emptyDescription}
                    variant="compact"
                  />
                </td>
              </tr>
            ) : pageRows.map((row, idx) => (
              <tr
                key={row[rowKey] ?? idx}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? styles.clickable : ''}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    onRowClick(row)
                  }
                }}
              >
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align || 'left' }}>
                    {c.render ? c.render(row[c.key], row, idx) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className={styles.pageBtn}
            aria-label={t('auto.unvui_datatable.prev_page', 'Предыдущая страница')}
          >
            <Icon name="chevron-left" size={16} />
          </button>
          <span className={styles.pageInfo}>
            {safePage + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className={styles.pageBtn}
            aria-label={t('auto.unvui_datatable.next_page', 'Следующая страница')}
          >
            <Icon name="chevron-right" size={16} />
          </button>
        </div>
      )}
    </div>
  )
}

export default DataTable
